import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useAuth } from "../../../context/AuthContext";
import { useAlert } from "../../../context/AlertContext";
import { useLoading } from "../../../context/LoadingContext";
import { formatInputDate } from "../../../utils/Time";
import { getServiceRequest } from "../../../commons/ServiceRequest";
import { initializeSetOption } from "../../../utils/InitializeSetDefault";
import {
  FloatingControlComponent,
  SearchSelectComponent
} from "../../FormComponent";
import { emptyValueObjects, findItemInArray } from "../../../utils/ManageArray";
import ConfirmModal from "../../ConfirmModal";
import TableForm from "../../TableForm";
import CustContractsService from "../../../services/custcontract.service";
import CardDisplayComponent from "../../CardDisplayComponent";

const SpecificationDetailsForm = ({ editData, onSubmitCallback }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const { setLoading } = useLoading();
  const [show, setShow] = useState(false);
  const [validated, setValidated] = useState(false);
  const [contract, setContract] = useState([]);
  const defaultData = {
    Hds: {
      Contract_No: "",
      Contract_Start_Date: "",
      Contract_End_Date: "",
      Con_Spec_Date: "",
      Cus_Id: "",
      Project_No: "",
      Com_Code: user.Com_Code
    },
    Dts: {
      Con_Spec_Hd_Id: "",
      Con_Spec_Check: true,
      Con_Spec_Des: "",
      PM_Of_Work: "",
      Remark: "",
      Com_Code: user.Com_Code
    }
  };
  const [data, setData] = useState({
    Hds: { Array: [defaultData.Hds] },
    Dts: { Array: [defaultData.Dts] }
  });
  const inputField = [
    {
      name: "Con_Spec_Des",
      label: "รายละเอียดข้อกำหนดเพิ่มเติมตามสัญญา",
      type: "text"
    },
    {
      name: "Remark",
      label: "ข้อกำหนดเพิ่มเติมอื่นๆ",
      type: "text",
      noneRequired: true
    },
    {
      name: "PM_Of_Work",
      label: "PM/ครั้งที่",
      type: "text",
      size: 2,
      pattern: /^[1-9]$/.source,
      feedbackMessage:
        "ข้อมูลไม่ถูกต้อง โปรดระบุจำนวนเต็ม และต้องที่ค่ามากกว่า 0 (จำนวนเต็มสูงสุด 1 ตัวอักษร)"
    }
  ];
  const searchInput = [
    {
      name: "Contract_Start_Date",
      label: "วันเริ่มสัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "date",
      disabled: true
    },
    {
      name: "Contract_End_Date",
      label: "วันสิ้นสุดสัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "date",
      disabled: true
    },
    {
      name: "Con_Spec_Date",
      label: "วันที่",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "date",
      readOnly: true
    },
    {
      name: "Customer.Cus_Code",
      label: "รหัสลูกค้าหลัก (BP Master Code)",
      type: "text",
      disabled: true
    },
    {
      name: "Customer.Cus_Name",
      label: "ชื่อลูกค้า",
      type: "text",
      disabled: true,
      size: 6
    },
    {
      name: "Project_No",
      label: "เลขที่โครงการ",
      type: "text",
      disabled: true
    }
  ];

  const autoDisplayValue = (name, type) => {
    const objValue = findItemInArray(
      contract,
      "Contract_No",
      data.Hds?.Array?.[0]?.Contract_No
    );

    if (objValue) {
      if (type === "date") {
        return data.Hds?.Array?.[0]?.[name];
      } else {
        if (name.split(".").length > 1) {
          return objValue?.[name.split(".")[0]]?.[name.split(".")[1]];
        } else {
          return objValue?.[name];
        }
      }
    }
  };

  const handleChange = (name, value) => {
    const objValue = findItemInArray(contract, name, value);

    setData((prev) => ({
      ...prev,
      Hds: {
        Array: [
          {
            ...data.Hds.Array[0],
            [name]: value,
            Contract_Start_Date: objValue
              ? formatInputDate(objValue.Contract_Start_Date, "Local").date
              : "",
            Contract_End_Date: objValue
              ? formatInputDate(objValue.Contract_End_Date, "Local").date
              : "",
            Con_Spec_Date: formatInputDate(new Date(), "Local").date,
            Cus_Id: objValue ? objValue.Cus_Id : "",
            Project_No: objValue ? objValue.Project_No : ""
          },
          ...data.Hds.Array.slice(1)
        ]
      }
    }));
  };

  const checkValidity = () => {
    const passEmptyKey = ["Con_Spec_Hd_Id", "Remark"];
    if (
      emptyValueObjects(data.Hds.Array).length > 0 ||
      emptyValueObjects(data.Dts.Array, passEmptyKey).length > 0
    ) {
      showAlert({
        error: "ข้อผิดพลาด",
        message: "กรุณากรอกข้อมูลให้ครบถ้วน",
        type: "danger"
      });
      return false;
    }
    return true;
  };

  const handleValidated = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity() || !checkValidity()) {
      event.stopPropagation();
      setValidated(true);
    } else {
      setShow(true);
    }
  };

  const handleSubmit = (event, { dataCallback, showCallback }) => {
    event.preventDefault();
    setShow(showCallback);

    if (dataCallback) {
      if (!editData) onSubmitCallback(dataCallback);
      else onSubmitCallback(dataCallback.Dts.Array[0]);
    }
  };

  useEffect(() => {
    if (editData?.Hds && editData?.Dts) {
      setData({
        Hds: {
          Array: [
            {
              Contract_No: editData?.Hds?.Contract_No,
              Contract_Start_Date: editData?.Hds?.Contract_Start_Date,
              Contract_End_Date: editData?.Hds?.Contract_End_Date,
              Con_Spec_Date: editData?.Hds?.Con_Spec_Date,
              Cus_Id: editData?.Hds?.Cus_Id,
              Project_No: editData?.Hds?.Project_No,
              Com_Code: editData?.Hds?.Com_Code
            }
          ]
        },
        Dts: {
          Array: [
            {
              Con_Spec_Hd_Id: editData?.Dts?.Con_Spec_Hd_Id,
              Con_Spec_Check: editData?.Dts?.Con_Spec_Check,
              Con_Spec_Des: editData?.Dts?.Con_Spec_Des,
              PM_Of_Work: editData?.Dts?.PM_Of_Work,
              Remark: editData?.Dts?.Remark,
              Com_Code: editData?.Dts?.Com_Code
            }
          ]
        }
      });
    }

    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: CustContractsService.getAllCustContracts(
        `?keyword=${editData?.Hds?.Contract_No}`
      ),
      callback: ({ items }) => setContract(items)
    });
  }, [editData]);

  return (
    <>
      <Form noValidate validated={validated} onSubmit={handleValidated}>
        <CardDisplayComponent
          body={{
            as: Row,
            element: (
              <>
                <Col sm={3} className="mb-3">
                  <SearchSelectComponent
                    labelField="เลขที่สัญญา"
                    floatingClass="mb-1"
                    placeholder="โปรดเลือก"
                    isInvalid={data.Hds?.Array?.[0]?.Contract_No === ""}
                    disabled={editData ? true : false}
                    options={initializeSetOption({
                      key: "Contract_No",
                      dataset: contract
                    })}
                    queryService={(query) =>
                      getServiceRequest({
                        showAlert,
                        setLoading,
                        serviceRequest:
                          CustContractsService.getAllCustContracts(query),
                        callback: ({ items }) => setContract(items)
                      })
                    }
                    onChange={(change) =>
                      handleChange("Contract_No", change?.value)
                    }
                    value={data.Hds?.Array?.[0]?.Contract_No}
                  />
                </Col>

                {searchInput.map(
                  ({
                    name,
                    label,
                    classNames,
                    type,
                    size,
                    readOnly,
                    disabled
                  }) => (
                    <Col key={name} sm={size || 3} className={classNames?.col}>
                      <FloatingControlComponent
                        type={type}
                        name={name}
                        label={label}
                        readOnly={readOnly}
                        disabled={disabled}
                        placeholder="โปรดระบุ"
                        className={classNames}
                        value={autoDisplayValue(name, type) || ""}
                      />
                    </Col>
                  )
                )}
              </>
            )
          }}
        />

        <CardDisplayComponent
          header={{
            title: {
              text: !editData ? "เพิ่มข้อกำหนดเพิ่มเติม" : "รายการแก้ไข"
            }
          }}
          body={{
            element: (
              <TableForm
                data={data.Dts.Array}
                columns={inputField}
                defaultData={defaultData.Dts}
                editForm={!!editData}
                onChangeCallback={(newData) =>
                  setData((prev) => ({ ...prev, Dts: { Array: newData } }))
                }
              />
            )
          }}
          footer={{
            className: "text-end",
            element: (
              <>
                <Button
                  type="submit"
                  variant="success"
                  className="me-2 px-5 py-2 text-dark rounded-pill"
                >
                  {editData ? "บันทึกการแก้ไข" : "บันทึกข้อมูล"}
                </Button>
                <ConfirmModal
                  show={show}
                  data={data}
                  content={editData ? "edit" : "save"}
                  onSubmitCallback={handleSubmit}
                />

                <Button
                  variant="outline-success"
                  className="px-5 py-2 text-dark border-dark border-opacity-50 rounded-pill"
                  onClick={() =>
                    navigate("/MA_service", { state: { name: "requirements" } })
                  }
                >
                  ยกเลิก
                </Button>
              </>
            )
          }}
        />
      </Form>
    </>
  );
};

SpecificationDetailsForm.propTypes = {
  editData: PropTypes.object,
  onSubmitCallback: PropTypes.func
};

export default SpecificationDetailsForm;
